import React, { useMemo } from 'react';
import type { SusuClient, SusuTransaction } from '@shared/types';
import { useClientTransactions } from '@/hooks/use-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface ClientHistoryDialogProps {
  client?: SusuClient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClientHistoryDialog({ client, open, onOpenChange }: ClientHistoryDialogProps) {
  const { data: txnsData, isLoading, error } = useClientTransactions(client?.id ?? '');

  const transactionsWithBalance = useMemo(() => {
    if (!txnsData?.items) return [];
    const chronTxns = [...txnsData.items].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    let currentSum = 0;
    const history = chronTxns.map((txn: SusuTransaction) => {
      if (txn.type === 'Collection') {
        currentSum += txn.amount;
      } else {
        currentSum -= txn.amount;
      }
      return { ...txn, runningBalance: currentSum };
    });
    return history.reverse();
  }, [txnsData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[2rem] border-border p-0 sm:max-w-3xl">
        <DialogHeader className="border-b border-border bg-muted/30 p-6">
          <DialogTitle className="text-xl font-black uppercase tracking-tight text-foreground">
            Transaction History
          </DialogTitle>
          <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {client ? `View all transactions for ${client.name}.` : 'Select a client to view their transaction history.'}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6">
          {error && (
            <Alert variant="destructive" className="mb-4 rounded-xl">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="font-black uppercase text-xs tracking-widest">Error</AlertTitle>
              <AlertDescription className="font-bold text-xs">
                Failed to load transaction history. Please try again.
              </AlertDescription>
            </Alert>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-card">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="border-border">
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4 pl-6">
                    Date
                  </TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    Type
                  </TableHead>
                  <TableHead className="text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    Amount (GHS)
                  </TableHead>
                  <TableHead className="text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground pr-6">
                    Balance
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="border-border">
                      <TableCell className="py-4 pl-6">
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right pr-6">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : transactionsWithBalance.length > 0 ? (
                  transactionsWithBalance.map((txn) => (
                    <TableRow key={txn.id} className="hover:bg-muted/50 transition-colors border-border">
                      <TableCell className="font-mono text-[10px] text-muted-foreground pl-6 py-4">
                        {new Date(txn.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {txn.type === 'Collection' ? (
                            <ArrowUpCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <ArrowDownCircle className="h-4 w-4 text-destructive" />
                          )}
                          <span className="font-black text-foreground text-xs uppercase tracking-tight">
                            {txn.type}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-right font-black',
                          txn.type === 'Collection' ? 'text-emerald-500' : 'text-destructive'
                        )}
                      >
                        {txn.type === 'Collection' ? '+' : '-'} {txn.amount.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-black text-foreground pr-6">
                        GHS {txn.runningBalance.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-48 text-center text-muted-foreground font-black uppercase tracking-[0.2em] text-[10px]"
                    >
                      No transactions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
